import { fromEvent, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { getElementsIntersectingRange, mutateElementsIntersectingRange } from '../../helpers/dom/element';
import { findPreviousNonWhitespaceSibling } from '../../helpers/dom/node';
import { isParagraphElement } from '../../helpers/dom/paragraph';
import { createPointWithStartOfRange } from '../../helpers/dom/point';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { firstInIterable, isEmptyIterable } from '../../helpers/iterable';
import { getParagraphIndentLevel, indentParagraph, outdentParagraph } from '../../helpers/paragraphIndent';
import { createConnectingService } from '../base/connectingService';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { IndentService, NULL_INDENT_SERVICE } from '../text/indentService';

export type HtmlParagraphIndentServiceOptions = {
  hasParagraphSpaceIndent?: boolean;
};

export function createHtmlParagraphIndentService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  options: HtmlParagraphIndentServiceOptions = {},
): IndentService {
  return createConnectingService(
    element$,
    () => NULL_INDENT_SERVICE,
    element => new HtmlParagraphIndentService(element, selectionService, options),
  );
}

export class HtmlParagraphIndentService extends ElementService<Element> implements IndentService {
  constructor(
    element: Element,
    selectionService: ElementSelectionService,
    options: HtmlParagraphIndentServiceOptions,
  ) {
    super(
      element,
      {
        effects$: fromEvent<KeyboardEvent>(element, 'keydown').pipe(
          ignoreDuringCompositions(element),
          tap(event => {
            if (event.defaultPrevented) {
              return;
            }

            const { key } = event;
            switch (key) {
              case 'Tab':
                if (event.shiftKey) {
                  if (this.getCanOutdent(key)) {
                    event.preventDefault();
                    this.outdent();
                  }
                } else if (this.getCanIndent(key)) {
                  event.preventDefault();
                  this.indent();
                }
                break;

              case 'Backspace':
                if (this.getCanOutdent(key)) {
                  event.preventDefault();
                  this.outdent();
                }
                break;

              default:
            }
          }),
          share(),
        ),
      },
      // TODO: Make changes$ more fine-grained.
      selectionService,
    );

    this.selectionService = selectionService;
    this.hasParagraphSpaceIndent = options.hasParagraphSpaceIndent || false;
  }

  private readonly selectionService: ElementSelectionService;
  private readonly hasParagraphSpaceIndent: boolean;

  get canIndent() {
    return this.getCanIndent();
  }

  private getCanIndent(key?: string) {
    return this.getCanChangeIndentLevel(0, key);
  }

  get canOutdent() {
    return this.getCanOutdent();
  }

  private getCanOutdent(key?: string) {
    return this.getCanChangeIndentLevel(1, key);
  }

  private getCanChangeIndentLevel(minimumIndentLevel: number, key?: string) {
    // Disable the service with the hasParagraphSpaceIndent FF, since the indentation is handled by htmlSpaceIndentService
    if (this.hasParagraphSpaceIndent) {
      return false;
    }
    const { selection, extraSelectedElements } = this.selectionService;
    if (!selection) {
      return false;
    }

    if (!isEmptyIterable(extraSelectedElements)) {
      return false;
    }

    const { range } = selection;

    // Handle backspace only if we are at a collapsed range.
    if (key === 'Backspace' && !range.collapsed) {
      return false;
    }

    const paragraphs = getElementsIntersectingRange(
      this.element,
      selection.range,
      (element =>
        element !== this.element &&
        isParagraphElement(element) &&
        getParagraphIndentLevel(element) >= minimumIndentLevel) as typeof isParagraphElement,
    );

    if (range.collapsed) {
      const paragraph = firstInIterable(paragraphs);

      // If the selection is collapsed, check we're at the start of the paragraph.
      return !!paragraph && (!key || this.getIsCollapsedAtStartOfParagraph(range, paragraph));
    }

    return !isEmptyIterable(paragraphs);
  }

  private getIsCollapsedAtStartOfParagraph(range: Range, paragraph: Node): boolean {
    return (
      range.collapsed &&
      createPointWithStartOfRange(range).isAtStartOfNode(
        paragraph,
        node => !findPreviousNonWhitespaceSibling(node),
        this.element,
      )
    );
  }

  indent() {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { range } = selection;
    mutateElementsIntersectingRange(this.element, range, isParagraphElement, paragraphs => {
      paragraphs.forEach(paragraph => {
        indentParagraph(paragraph);
      });
    });
  }

  outdent() {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { range } = selection;
    mutateElementsIntersectingRange(this.element, range, isParagraphElement, paragraphs => {
      paragraphs.forEach(paragraph => {
        outdentParagraph(paragraph);
      });
    });
  }
}
